import { useEffect, useRef, useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { fmtMoney, TH_MONTHS_ABBR } from '../../lib/expense.js'

function fmtShort(n) {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)}k`
  return `${Math.round(v)}`
}
function niceMax(m) {
  if (m <= 0) return 5
  const p = Math.pow(10, Math.floor(Math.log10(m)))
  return [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((x) => x * p).find((s) => s >= m) || 10 * p
}

// Monthly expense-total trend (dependency-free SVG line chart). Data = [{year, month, amount}].
export default function ExpenseTrendChart({ data = [], loading }) {
  const ref = useRef(null)
  const [w, setW] = useState(800)
  const [hoverIdx, setHoverIdx] = useState(null)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((e) => { const cw = e[0]?.contentRect?.width; if (cw) setW(Math.max(320, Math.round(cw))) })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const pts = [...(data || [])]
    .filter((p) => p && p.year && p.month)
    .sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month))
  const H = 190, padL = 46, padR = 14, padT = 14, padB = 26
  const innerW = w - padL - padR, innerH = H - padT - padB
  const n = pts.length
  const max = niceMax(Math.max(...pts.map((p) => Number(p.amount) || 0), 0))
  const X = (i) => padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW)
  const Y = (v) => padT + (1 - (Number(v) || 0) / max) * innerH
  const linePts = pts.map((p, i) => `${X(i).toFixed(1)},${Y(p.amount).toFixed(1)}`).join(' ')
  const areaPts = n ? `${X(0).toFixed(1)},${Y(0).toFixed(1)} ${linePts} ${X(n - 1).toFixed(1)},${Y(0).toFixed(1)}` : ''
  const rings = [0, 0.25, 0.5, 0.75, 1]
  const step = Math.max(1, Math.ceil(n / 8))
  const labelIdx = pts.map((_, i) => i).filter((i) => i % step === 0 || i === n - 1)
  const latest = pts[n - 1]
  const hp = hoverIdx != null ? pts[hoverIdx] : null

  function onMove(e) {
    if (!ref.current || !n) return
    const rect = ref.current.getBoundingClientRect()
    const mx = (e.clientX - rect.left) * (w / (rect.width || w)) // → viewBox x
    let idx = n <= 1 ? 0 : Math.round(((mx - padL) / innerW) * (n - 1))
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)))
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-brand-600" />
          <h3 className="text-[14px] font-semibold text-slate-800">แนวโน้มค่าใช้จ่ายรายเดือน</h3>
        </div>
        {latest && !loading && (
          <div className="text-[12px] text-slate-400">
            ล่าสุด <b className="text-slate-700 tabular-nums">{fmtMoney(latest.amount)}</b> ({TH_MONTHS_ABBR[latest.month - 1]} {latest.year + 543})
          </div>
        )}
      </div>
      <div ref={ref} className="w-full relative" onMouseMove={onMove} onMouseLeave={() => setHoverIdx(null)}>
        {/* Hover tooltip */}
        {hp && !loading && (
          <div
            className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg bg-slate-800 px-2.5 py-1.5 text-white shadow-lg"
            style={{ left: `${(X(hoverIdx) / w) * 100}%`, top: 0 }}
          >
            <div className="text-[10.5px] text-slate-300 whitespace-nowrap">{TH_MONTHS_ABBR[hp.month - 1]} {hp.year + 543}</div>
            <div className="text-[13px] font-semibold tabular-nums whitespace-nowrap">{fmtMoney(hp.amount)}</div>
          </div>
        )}
        {loading ? (
          <div className="h-[190px] flex items-center justify-center text-[13px] text-slate-400">กำลังโหลด…</div>
        ) : !n ? (
          <div className="h-[190px] flex items-center justify-center text-[13px] text-slate-400">ไม่มีข้อมูล</div>
        ) : (
          <svg width="100%" height={H} viewBox={`0 0 ${w} ${H}`}>
            <defs>
              <linearGradient id="expTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity="0.16" />
                <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
              </linearGradient>
            </defs>
            {rings.map((f, i) => {
              const yy = padT + (1 - f) * innerH
              return (
                <g key={i}>
                  <line x1={padL} y1={yy} x2={w - padR} y2={yy} stroke="#eef2f7" strokeWidth={1} />
                  <text x={padL - 6} y={yy} dy="0.32em" textAnchor="end" fontSize="9.5" className="fill-slate-300">{fmtShort(max * f)}</text>
                </g>
              )
            })}
            <polygon points={areaPts} fill="url(#expTrendFill)" />
            <polyline points={linePts} fill="none" stroke="#2563eb" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            {pts.map((p, i) => (
              <circle key={i} cx={X(i)} cy={Y(p.amount)} r={2.5} fill="#fff" stroke="#2563eb" strokeWidth={1.5}>
                <title>{`${TH_MONTHS_ABBR[p.month - 1]} ${p.year + 543}: ${fmtMoney(p.amount)}`}</title>
              </circle>
            ))}
            {labelIdx.map((i) => {
              const p = pts[i]
              return <text key={i} x={X(i)} y={H - 8} textAnchor="middle" fontSize="9.5" className="fill-slate-400">{TH_MONTHS_ABBR[p.month - 1]} {String(p.year + 543).slice(-2)}</text>
            })}
            {hp && (
              <g>
                <line x1={X(hoverIdx)} y1={padT} x2={X(hoverIdx)} y2={H - padB} stroke="#93c5fd" strokeWidth={1} strokeDasharray="3 3" />
                <circle cx={X(hoverIdx)} cy={Y(hp.amount)} r={4} fill="#2563eb" stroke="#fff" strokeWidth={2} />
              </g>
            )}
          </svg>
        )}
      </div>
    </section>
  )
}
